import { Dispatch, SetStateAction } from "react";
import { IAcquisitionContext, IAuthorizedUserValidity } from "../../providers/AcquisitionProvider/AcquisitionProvider";
import {
  IAuthorizedUser,
  IPersonalInfo,
  IPersonalInfoErrors,
  IPhoneAndIncome,
} from "../../providers/AcquisitionProvider/models";
import {
  TInputField,
  TAcquisitionState,
  TBooleanField,
} from "../../providers/ModernAcquisitionProvider/types";

export type TLegacyWriteManager = Pick<IAcquisitionContext, 'setPersonalInfoForm'|'setPersonalInfoErrors'|'setAuthorizedUserInfo'|'setAuthorizedUserValidity'>;

/**
 * TODO:
 * -Authorized user
 *  Same person error and isAUSamePersonCheck in legacy needs additional attention
 * -[ssnInfo, setSsnInfo]
 * -[isMonthlyIncomeValid, setIsMonthlyIncomeValid]
 * -[fundingInfo, setFundingInfo]
 * -[selectedCardId, setSelectedCardId]
 */
const legacyWriteManager = (
  setPersonalInfoForm: Dispatch<SetStateAction<IPersonalInfo>>,
  setPersonalInfoErrors: Dispatch<SetStateAction<IPersonalInfoErrors>>,
  setAuthorizedUserInfo: Dispatch<SetStateAction<IAuthorizedUser>>,
  setAuthorizedUserValidity: Dispatch<SetStateAction<IAuthorizedUserValidity>>,
  setIsPhoneNumberValid: Dispatch<SetStateAction<boolean>>,
  setPhoneAndIncome: Dispatch<SetStateAction<IPhoneAndIncome>>,
) => {
  const write = (updates: TAcquisitionState) => {
    const hasLegacyAuthorizedUserUpdate = [
      'authorizedUserFirstName',
      'authorizedUserLastName',
      'authorizedUserMiddleInitial',
      'authorizedUserDateOfBirthDay',
      'authorizedUserDateOfBirthMonth',
      'authorizedUserDateOfBirthYear',
      'authorizedUserSpouse',
    ].some(key => key in updates);
    const hasLegacyFormUpdate = [
      'emailAddress',
      'firstName',
      'homeAddressLineOne',
      'homeAddressLineTwo',
      'homeAddressCity',
      'homeAddressState',
      'homeAddressZipCode',
      'lastName',
      'mailingAddressLineOne',
      'mailingAddressLineTwo',
      'mailingAddressCity',
      'mailingAddressState',
      'mailingAddressZipCode',
      'middleInitial',
      'monthlyIncome',
      'phoneNumber',
      'ssn',
      'suffix',
      'dateOfBirthDay',
      'dateOfBirthMonth',
      'dateOfBirthYear',
    ].some(key => key in updates);
    const hasPhoneFormUpdate = [
      'phoneNumber'
    ].some(key => key in updates);
    const hasPhoneOrIncomeUpdate = [
      'phoneNumber',
      'monthlyIncome'
    ].some(key => key in updates);

    const transformToPersonalInfoFormSchema = (obj: IPersonalInfo, personalInfoFormKey: keyof IPersonalInfo, updates: Partial<TInputField>) => {
      if (Object.prototype.hasOwnProperty.call(updates, 'value')) {
        (obj[personalInfoFormKey] as { value: string; valid: boolean; }).value = updates.value;
      }
      if (Object.prototype.hasOwnProperty.call(updates, 'state')) {
        if (updates.state === 'neutral' || updates.state === 'success') {
          (obj[personalInfoFormKey] as { value: string; valid: boolean; }).valid = true;
        } else {
          (obj[personalInfoFormKey] as { value: string; valid: boolean; }).valid = false;
        }
      }
    }

    const transformToPersonalInfoErrorsSchema = (obj: IPersonalInfoErrors, personalInfoErrorKey: keyof IPersonalInfoErrors, updates: Partial<TInputField>) => {

      if (Object.prototype.hasOwnProperty.call(updates, 'errorType')) {
        switch (updates.errorType) {
          case 'requiredField': obj[personalInfoErrorKey] = 'RequiredField'; break;
          case 'invalidInput': obj[personalInfoErrorKey] = 'NotValid'; break;
          case 'thirdParty': obj[personalInfoErrorKey] = 'ThirdParty'; break;
          case 'notEligible': obj[personalInfoErrorKey] = 'NotEligible'; break;
          case 'None':
          default: obj[personalInfoErrorKey] = 'None'; break;
        }
      }
    }

    const transformToAuthorizedUserSchema = (obj: IAuthorizedUser, authorizedUserKey: keyof IAuthorizedUser, updates: Partial<TInputField>) => {
      if (Object.prototype.hasOwnProperty.call(updates, 'value')) {
        // use Object.assign to avoid typing error since IAuthorizedUser contains an optional type
        Object.assign(obj, { [authorizedUserKey]: updates.value });
      }
    }

    const transformToAuthorizedUserValiditySchema = (obj: IAuthorizedUserValidity, authorizedUserValidityKey: keyof IAuthorizedUserValidity, updates: Partial<TInputField>) => {
      if (Object.prototype.hasOwnProperty.call(updates, 'state')) {
        if (updates.state === 'neutral' || updates.state === 'success') {
          obj[authorizedUserValidityKey] = true;
        } else {
          obj[authorizedUserValidityKey] = false;
        }
      }
    }

    const transformToPhoneAndIncomeSchema = (obj: IPhoneAndIncome, phoneAndIncomeKey: keyof IPhoneAndIncome, updates: Partial<TInputField>) => {
      if (Object.prototype.hasOwnProperty.call(updates, 'value')) {
        Object.assign(obj, { [phoneAndIncomeKey]: updates.value });
      }
    }

    if (hasLegacyAuthorizedUserUpdate) {
      setAuthorizedUserInfo((prev) => {
        (Object.entries(updates) as [keyof TAcquisitionState, Partial<TInputField> & Partial<TBooleanField>][]).forEach(
          ([key, value]) => {
            switch (key) {
              case "authorizedUserFirstName":
                transformToAuthorizedUserSchema(prev, 'AuthorizedUserFirstName', value);
                break;
              case "authorizedUserLastName":
                transformToAuthorizedUserSchema(prev, 'AuthorizedUserLastName', value);
                break;
              case "authorizedUserMiddleInitial":
                transformToAuthorizedUserSchema(prev, 'AuthorizedUserMiddleInitial', value);
                break;
              case 'authorizedUserSpouse':
                transformToAuthorizedUserSchema(prev, 'IsAuthorizedUserSpouse', value);
                break;
              default:
                break;
            }
          },
        );

        if (
          Object.prototype.hasOwnProperty.call(updates, 'authorizedUserDateOfBirthYear') &&
          Object.prototype.hasOwnProperty.call(updates['authorizedUserDateOfBirthYear'], 'value')
        ) {
          prev['AuthorizedUserDOBYear'] = updates['authorizedUserDateOfBirthYear'].value;
        }

        if (
          Object.prototype.hasOwnProperty.call(updates, 'authorizedUserDateOfBirthMonth') &&
          Object.prototype.hasOwnProperty.call(updates['authorizedUserDateOfBirthMonth'], 'value')
        ) {
          prev['AuthorizedUserDOBMonth'] = updates['authorizedUserDateOfBirthMonth'].value;
        }

        if (
          Object.prototype.hasOwnProperty.call(updates, 'authorizedUserDateOfBirthDay') &&
          Object.prototype.hasOwnProperty.call(updates['authorizedUserDateOfBirthDay'], 'value')
        ) {
          prev['AuthorizedUserDOBDay'] = updates['authorizedUserDateOfBirthDay'].value;
        }

        return { ...prev };
      });
      setAuthorizedUserValidity((prev) => {

        (Object.entries(updates) as [keyof TAcquisitionState, Partial<TInputField> & Partial<TBooleanField>][]).forEach(
          ([key, value]) => {
            switch (key) {
              case "authorizedUserFirstName":
                transformToAuthorizedUserValiditySchema(prev, 'authorizedUserFirstName', value);
                break;
              case "authorizedUserLastName":
                transformToAuthorizedUserValiditySchema(prev, 'authorizedUserLastName', value);
                break;
              case 'authorizedUserSpouse':
                transformToAuthorizedUserValiditySchema(prev, 'authorizedUserSpouse', value);
                break;
              default:
                break;
            }
          },
        );

        const authorizedUserDOBFields = ['authorizedUserDateOfBirthYear', 'authorizedUserDateOfBirthMonth', 'authorizedUserDateOfBirthDay'];
        if (
          authorizedUserDOBFields.every(k => Object.prototype.hasOwnProperty.call(updates, k)) &&
          authorizedUserDOBFields.every(k => Object.prototype.hasOwnProperty.call(updates[k], 'state'))
        ) {
          // any field in error indicates dob is invalid
          if (authorizedUserDOBFields.some(k => updates[k] === 'error')) {
            prev['authorizedUserDob'] = false;
          } else { // neutral or success is valid
            prev['authorizedUserDob'] = true;
          }
        }

        return { ...prev };
      });
    }

    if (hasLegacyFormUpdate) {
      setPersonalInfoForm((prev) => {
        (Object.entries(updates) as [keyof TAcquisitionState, Partial<TInputField> & Partial<TBooleanField>][]).forEach(
          ([key, value]) => {
            switch (key) {
              case "emailAddress":
                transformToPersonalInfoFormSchema(prev, 'email', value);
                break;
              case "firstName":
                transformToPersonalInfoFormSchema(prev, 'firstName', value);
                break;
              case "homeAddressLineOne":
                transformToPersonalInfoFormSchema(prev, 'homeAddress', value);  
                break;
              case "homeAddressLineTwo":
                transformToPersonalInfoFormSchema(prev, 'homeAptUnit', value);  
                break;
              case "homeAddressCity":
                transformToPersonalInfoFormSchema(prev, 'homeCity', value);  
                break;
              case "homeAddressState":
                transformToPersonalInfoFormSchema(prev, 'homeState', value);  
                break;
              case "homeAddressZipCode":
                transformToPersonalInfoFormSchema(prev, 'homeZip', value);  
                break;
              case "lastName":
                transformToPersonalInfoFormSchema(prev, 'lastName', value);  
                break;
              case "mailingAddressLineOne":
                transformToPersonalInfoFormSchema(prev, 'mailingAddress', value);  
                break;
              case "mailingAddressLineTwo":
                transformToPersonalInfoFormSchema(prev, 'mailingAptUnit', value);  
                break;
              case "mailingAddressCity":
                transformToPersonalInfoFormSchema(prev, 'mailingCity', value);  
                break;
              case "mailingAddressState":
                transformToPersonalInfoFormSchema(prev, 'mailingState', value);  
                break;
              case "mailingAddressZipCode":
                transformToPersonalInfoFormSchema(prev, 'mailingZip', value);  
                break;
              case "middleInitial":
                // always optional field
                transformToPersonalInfoFormSchema(prev, 'middleInitial', { ...value, errorType: 'None'});
                break;
              case "monthlyIncome":
                transformToPersonalInfoFormSchema(prev, 'monthlyIncome', value);  
                break;
              case "phoneNumber":
                transformToPersonalInfoFormSchema(prev, 'phoneNumber', value);  
                break;
              case "ssn":
                transformToPersonalInfoFormSchema(prev, 'ssn', value);  
                break;
              case "suffix":
                // always optional field
                transformToPersonalInfoFormSchema(prev, 'suffix', { ...value, errorType: 'None'});
                break;
              default:
                break;
            }
          },
        );

        const personalInfoFormDOBFields = ['dateOfBirthYear', 'dateOfBirthMonth', 'dateOfBirthDay'];
        if (personalInfoFormDOBFields.every(k => Object.prototype.hasOwnProperty.call(updates, k))) {
          // all fields have 'value' property
          if (personalInfoFormDOBFields.every(k => Object.prototype.hasOwnProperty.call(updates[k], 'value'))) {
            prev['dob'].value = 
                `${updates.dateOfBirthMonth?.value ?? ''}/${updates.dateOfBirthDay?.value ?? ''}/${updates.dateOfBirthYear?.value ?? ''}`;
          }

          // all fields have 'state' property
          if (personalInfoFormDOBFields.every(k => Object.prototype.hasOwnProperty.call(updates[k], 'state'))) {
            // any field in error indicates dob is invalid
            if (personalInfoFormDOBFields.some(k => (updates[k] as Partial<TInputField>).state === 'error')) {
              prev['dob'].valid = false;
            } else { // neutral or success is valid
              prev['dob'].valid = true;
            }
          }
        }

        return { ...prev };
      });
      setPersonalInfoErrors((prev) => {
        (Object.entries(updates) as [keyof TAcquisitionState, Partial<TInputField> & Partial<TBooleanField>][]).forEach(
          ([key, value]) => {
            switch (key) {
              case "emailAddress":
                transformToPersonalInfoErrorsSchema(prev, 'EmailAddress', value);
                break;
              case "firstName":
                transformToPersonalInfoErrorsSchema(prev, 'FirstName', value);
                break;
              case "homeAddressLineOne":
                transformToPersonalInfoErrorsSchema(prev, 'HomeAddressLine1', value);  
                break;
              case "homeAddressLineTwo":
                transformToPersonalInfoErrorsSchema(prev, 'HomeAddressLine2', value);  
                break;
              case "homeAddressCity":
                transformToPersonalInfoErrorsSchema(prev, 'HomeCity', value);  
                break;
              case "homeAddressState":
                transformToPersonalInfoErrorsSchema(prev, 'HomeState', value);  
                break;
              case "homeAddressZipCode":
                transformToPersonalInfoErrorsSchema(prev, 'HomeZipCode', value);  
                break;
              case "lastName":
                transformToPersonalInfoErrorsSchema(prev, 'LastName', value);  
                break;
              case "mailingAddressLineOne":
                transformToPersonalInfoErrorsSchema(prev, 'MailingAddressLine1', value);  
                break;
              case "mailingAddressLineTwo":
                transformToPersonalInfoErrorsSchema(prev, 'MailingAddressLine2', value);  
                break;
              case "mailingAddressCity":
                transformToPersonalInfoErrorsSchema(prev, 'MailingCity', value);  
                break;
              case "mailingAddressState":
                transformToPersonalInfoErrorsSchema(prev, 'MailingState', value);  
                break;
              case "mailingAddressZipCode":
                transformToPersonalInfoErrorsSchema(prev, 'MailingZipCode', value);  
                break;
              case "middleInitial":
                // always optional field
                transformToPersonalInfoErrorsSchema(prev, 'MiddleInitial', { ...value, errorType: 'None'});
                break;
              case "monthlyIncome":
                transformToPersonalInfoErrorsSchema(prev, 'MonthlyIncome', value);  
                break;
              case "phoneNumber":
                transformToPersonalInfoErrorsSchema(prev, 'PrimaryPhoneNumber', value);  
                break;
              case "ssn":
                transformToPersonalInfoErrorsSchema(prev, 'Ssn', value);  
                break;
              case "suffix":
                // always optional field
                transformToPersonalInfoErrorsSchema(prev, 'NameSuffix', { ...value, errorType: 'None'});
                break;
              default:
                break;
            }
          },
        );

        const personalInfoFormDOBFields = ['dateOfBirthYear', 'dateOfBirthMonth', 'dateOfBirthDay'];
        if (personalInfoFormDOBFields.every(k => Object.prototype.hasOwnProperty.call(updates, k))) {
          // all fields have 'errorType' property and match
          // if they do not match we have no strategy to resolve to a unified value
          if (
            personalInfoFormDOBFields.every(k => Object.prototype.hasOwnProperty.call(updates[k], 'errorType')) &&
            (updates[personalInfoFormDOBFields[0]] as Partial<TInputField>).errorType === (updates[personalInfoFormDOBFields[1]] as Partial<TInputField>).errorType &&
            (updates[personalInfoFormDOBFields[0]] as Partial<TInputField>).errorType === (updates[personalInfoFormDOBFields[2]] as Partial<TInputField>).errorType
          ) {
            switch ((updates[personalInfoFormDOBFields[0]] as Partial<TInputField>).errorType) {
              case 'requiredField': prev['DateOfBirth'] = 'RequiredField'; break;
              case 'invalidInput': prev['DateOfBirth'] = 'NotValid'; break;
              case 'notEligible': prev['DateOfBirth'] = 'NotEligible'; break;
              case 'None':
              default: prev['DateOfBirth'] = 'None'; break;
            }
          }
        }

        return { ...prev };
      });

      if (hasPhoneOrIncomeUpdate) {
        if (hasPhoneFormUpdate) {
          setIsPhoneNumberValid(() => updates.phoneNumber.errorType === 'None' && updates.phoneNumber.state !== 'error');
          setPhoneAndIncome((prev) => {
            (Object.entries(updates) as [keyof TAcquisitionState, Partial<TInputField>][]).forEach(
              ([key, value]) => {
                switch (key) {
                  case "phoneNumber":
                    transformToPhoneAndIncomeSchema(prev, 'phone', value);
                    break;
                  case "monthlyIncome":
                    transformToPhoneAndIncomeSchema(prev, 'income', value);
                    break;
                  default:
                    break;
                }
              },
            );
            return { ...prev };
          })
        }
      }      
    }
  };

  return {
    write,
  };
};

export default legacyWriteManager;
